import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { adminApi } from '../../utils/api';
import { Button, Badge, Spinner } from './shared';

const toISODate = (d) => d.toISOString().split('T')[0];

const PERIOD_PRESETS = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: '7', label: '7 days' },
    { id: '14', label: '14 days' },
    { id: '30', label: '30 days' },
    { id: '90', label: '90 days' },
    { id: 'custom', label: 'Custom' },
];

const VALID_PERIODS = new Set(['today', 'yesterday', '7', '14', '30', '90', 'custom']);

const getDateRange = (presetId, customStart, customEnd) => {
    const today = new Date();
    if (presetId === 'today') {
        const d = toISODate(today);
        return { start_date: d, end_date: d };
    }
    if (presetId === 'yesterday') {
        const y = new Date(today);
        y.setDate(y.getDate() - 1);
        const d = toISODate(y);
        return { start_date: d, end_date: d };
    }
    if (presetId === 'custom' && customStart && customEnd) {
        return { start_date: customStart, end_date: customEnd };
    }
    const days = parseInt(presetId);
    const start = new Date(today);
    start.setDate(start.getDate() - days);
    return { start_date: toISODate(start), end_date: toISODate(today) };
};

const getPrevDateRange = (dateRange) => {
    const start = new Date(dateRange.start_date);
    const end = new Date(dateRange.end_date);
    const diffMs = end.getTime() - start.getTime();
    const diffDays = Math.max(Math.round(diffMs / 86400000), 1);
    const prevEnd = new Date(start);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevStart.getDate() - diffDays + 1);
    return { start_date: toISODate(prevStart), end_date: toISODate(prevEnd) };
};

const fmt = (n, decimals = 2) => {
    if (n == null) return '0';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return typeof n === 'number' && !Number.isInteger(n) ? n.toFixed(decimals) : String(n);
};

const fmtCost = (n) => `$${(n || 0).toFixed(4)}`;

const fmtMs = (n) => {
    if (n == null) return '-';
    if (n >= 1000) return `${(n / 1000).toFixed(1)}s`;
    return `${Math.round(n)}ms`;
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const AREA_COLORS = [
    '#111827', '#6366f1', '#f59e0b', '#10b981', '#ef4444',
    '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#64748b',
];

const DONUT_COLORS = [
    '#111827', '#6366f1', '#f59e0b', '#10b981', '#ef4444',
    '#8b5cf6', '#ec4899', '#14b8a6',
];

function PeriodSelector({ preset, onPresetChange, customStart, customEnd, onCustomChange }) {
    return (
        <div className="flex items-center gap-1.5 flex-wrap">
            {PERIOD_PRESETS.map((p) => (
                <button
                    key={p.id}
                    type="button"
                    onClick={() => onPresetChange(p.id)}
                    className={`h-7 px-2.5 text-xs rounded-md border transition-colors ${
                        preset === p.id
                            ? 'bg-gray-900 text-white border-gray-900'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:text-gray-900'
                    }`}
                >
                    {p.label}
                </button>
            ))}
            {preset === 'custom' && (
                <div className="flex items-center gap-1.5 ml-1">
                    <input
                        type="date"
                        value={customStart}
                        onChange={(e) => onCustomChange(e.target.value, customEnd)}
                        className="h-7 px-2 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                    />
                    <span className="text-xs text-gray-400">-</span>
                    <input
                        type="date"
                        value={customEnd}
                        onChange={(e) => onCustomChange(customStart, e.target.value)}
                        className="h-7 px-2 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                    />
                </div>
            )}
        </div>
    );
}

function ChangeIndicator({ current, previous }) {
    if (previous == null || current == null || previous === 0) return null;
    const pct = ((current - previous) / previous) * 100;
    if (Math.abs(pct) < 0.1) return null;
    const isUp = pct > 0;
    return (
        <span className={`inline-flex items-center text-[10px] font-medium ${isUp ? 'text-red-500' : 'text-emerald-500'}`}>
            {isUp ? '+' : ''}{pct.toFixed(1)}%
        </span>
    );
}

function StatCard({ label, value, sub, variant, change, to }) {
    const borderClass = variant === 'success' ? 'border-emerald-200 bg-emerald-50/50'
        : variant === 'warning' ? 'border-amber-200 bg-amber-50/50'
        : variant === 'danger' ? 'border-red-200 bg-red-50/50'
        : 'border-gray-200 bg-white';
    const content = (
        <>
            <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">{label}</p>
                {change}
            </div>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </>
    );
    const cls = `rounded-lg border px-5 py-4 ${borderClass}`;
    if (to) {
        return <Link to={to} className={`${cls} block hover:shadow-sm transition-shadow`}>{content}</Link>;
    }
    return <div className={cls}>{content}</div>;
}

function CostBar({ value, max }) {
    const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    return (
        <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-mono text-gray-700 w-20 text-right shrink-0">{fmtCost(value)}</span>
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gray-900 rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

function DropdownSelect({ value, onChange, options, className }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const current = options.find((o) => o.value === value);
    return (
        <div ref={ref} className={`relative ${className || ''}`}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="h-7 px-2.5 text-xs rounded-md border border-gray-200 bg-white text-gray-700 hover:border-gray-300 flex items-center gap-1"
            >
                {current?.label || value}
                <svg className={`w-3 h-3 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[120px]">
                    {options.map((o) => (
                        <button
                            key={o.value}
                            type="button"
                            onClick={() => { onChange(o.value); setOpen(false); }}
                            className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${o.value === value ? 'font-medium text-gray-900' : 'text-gray-600'}`}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function DailyUsageChart({ dateRange }) {
    const chartRef = useRef(null);
    const [groupBy, setGroupBy] = useState('model');
    const [metric, setMetric] = useState('spend');
    const [chartType, setChartType] = useState('area');
    const [breakdown, setBreakdown] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        adminApi.getTokenUsageDailyBreakdown({ ...dateRange, group_by: groupBy })
            .then(setBreakdown)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dateRange, groupBy]);

    if (loading) return (
        <div className="border border-gray-200 rounded-lg bg-white p-6">
            <Spinner text="Loading chart..." center />
        </div>
    );
    if (!breakdown || breakdown.dates.length === 0) return null;

    const categories = breakdown.dates.map((d) => {
        const parts = d.split('-');
        return `${parts[1]}-${parts[2]}`;
    });
    const todayStr = toISODate(new Date());
    const todayIdx = breakdown.dates.indexOf(todayStr);

    const isCost = metric === 'spend';
    const yLabel = isCost ? 'Spend' : 'Tokens';
    const yFormat = isCost ? '${value}' : '{value}';

    const series = breakdown.series.map((s, i) => ({
        name: s.name,
        data: isCost ? s.cost : s.tokens,
        color: AREA_COLORS[i % AREA_COLORS.length],
        ...(chartType === 'area' ? { type: 'area', fillOpacity: 0.08, lineWidth: 2 } : { type: 'column' }),
    }));

    const options = {
        chart: { height: 280, style: { fontFamily: 'inherit' }, spacingTop: 10 },
        title: { text: null },
        credits: { enabled: false },
        xAxis: {
            categories,
            crosshair: { width: 1, color: '#d1d5db', dashStyle: 'Dash' },
            labels: { style: { fontSize: '10px', color: '#9ca3af' }, rotation: breakdown.dates.length > 20 ? -45 : 0 },
            lineColor: '#e5e7eb',
            tickLength: 0,
            plotLines: todayIdx >= 0 ? [{
                value: todayIdx, color: '#9ca3af', dashStyle: 'Dash', width: 1, zIndex: 5,
                label: { text: 'Today', rotation: 0, style: { fontSize: '10px', color: '#6b7280' }, align: 'left', x: 4, y: 12 },
            }] : [],
        },
        yAxis: {
            title: { text: yLabel, style: { fontSize: '10px', color: '#9ca3af', fontWeight: '400' } },
            labels: { style: { fontSize: '10px', color: '#9ca3af' }, format: yFormat },
            gridLineColor: '#f3f4f6', min: 0,
        },
        tooltip: {
            shared: true, style: { fontSize: '11px' },
            valuePrefix: isCost ? '$' : '', valueSuffix: isCost ? '' : ' tokens',
            valueDecimals: isCost ? 4 : 0,
        },
        plotOptions: {
            area: { marker: { enabled: false, symbol: 'circle', radius: 3, states: { hover: { enabled: true } } } },
            column: { borderRadius: 2, borderWidth: 0, groupPadding: 0.15 },
            series: { stacking: chartType === 'area' ? undefined : 'normal' },
        },
        legend: {
            align: 'left', verticalAlign: 'bottom', symbolRadius: 2, symbolHeight: 8, symbolWidth: 12,
            itemStyle: { fontSize: '10px', color: '#6b7280', fontWeight: '400' },
        },
        series,
    };

    return (
        <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-medium text-gray-900">Daily Usage Trend</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Cost and token usage over time</p>
                </div>
                <div className="flex items-center gap-1.5">
                    <DropdownSelect value={groupBy} onChange={setGroupBy} options={[{ value: 'model', label: 'By Model' }, { value: 'agent', label: 'By Agent' }]} />
                    <DropdownSelect value={metric} onChange={setMetric} options={[{ value: 'spend', label: 'Spend' }, { value: 'tokens', label: 'Tokens' }]} />
                    <DropdownSelect value={chartType} onChange={setChartType} options={[{ value: 'area', label: 'Area' }, { value: 'column', label: 'Bar' }]} />
                </div>
            </div>
            <div className="px-2">
                <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
            </div>
        </div>
    );
}

function CostDonutChart({ models }) {
    const chartRef = useRef(null);
    if (!models || models.length === 0) return null;

    const totalCost = models.reduce((s, m) => s + m.total_cost, 0);
    if (totalCost === 0) return null;

    const data = models.map((m, i) => ({
        name: m.model_name,
        y: m.total_cost,
        color: DONUT_COLORS[i % DONUT_COLORS.length],
    }));

    const options = {
        chart: { type: 'pie', height: 240, style: { fontFamily: 'inherit' } },
        title: { text: null },
        credits: { enabled: false },
        tooltip: {
            style: { fontSize: '11px' },
            pointFormat: '<b>{point.percentage:.1f}%</b> — ${point.y:.4f}',
        },
        plotOptions: {
            pie: {
                innerSize: '55%',
                borderWidth: 0,
                dataLabels: {
                    enabled: true,
                    format: '{point.name}',
                    style: { fontSize: '10px', color: '#6b7280', fontWeight: '400', textOutline: 'none' },
                    distance: 12,
                },
            },
        },
        series: [{ data }],
    };

    return (
        <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">Cost Distribution</h3>
                <span className="text-xs text-gray-400">{fmtCost(totalCost)} total</span>
            </div>
            <div className="px-2">
                <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
            </div>
        </div>
    );
}

function HourlyHeatmapChart({ heatmap, peakHour, peakDay }) {
    if (!heatmap || heatmap.length === 0) return null;
    const maxReqs = Math.max(...heatmap.map(h => h.requests), 1);
    const grid = {};
    heatmap.forEach((h) => { grid[`${h.day}-${h.hour}`] = h; });

    return (
        <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">Activity Heatmap</h3>
                {peakHour != null && peakDay != null && (
                    <span className="text-xs text-gray-400">Peak: {DAY_NAMES[peakDay]} {peakHour}:00</span>
                )}
            </div>
            <div className="px-4 py-3 overflow-x-auto">
                <table className="w-full border-collapse">
                    <thead>
                        <tr>
                            <th className="text-[10px] text-gray-400 font-normal w-8" />
                            {Array.from({ length: 24 }, (_, i) => (
                                <th key={i} className="text-[10px] text-gray-400 font-normal px-0 text-center w-6">{i}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                            <tr key={day}>
                                <td className="text-[10px] text-gray-400 pr-1">{DAY_NAMES[day]}</td>
                                {Array.from({ length: 24 }, (_, hour) => {
                                    const cell = grid[`${day}-${hour}`];
                                    const intensity = cell ? cell.requests / maxReqs : 0;
                                    const bg = intensity === 0 ? 'bg-gray-50'
                                        : intensity < 0.25 ? 'bg-gray-200'
                                        : intensity < 0.5 ? 'bg-gray-400'
                                        : intensity < 0.75 ? 'bg-gray-600'
                                        : 'bg-gray-900';
                                    return (
                                        <td key={hour} className="p-0.5">
                                            <div
                                                className={`w-full h-4 rounded-sm ${bg} cursor-default`}
                                                title={cell ? `${DAY_NAMES[day]} ${hour}:00 — ${cell.requests} reqs, ${fmtCost(cell.cost)}` : `${DAY_NAMES[day]} ${hour}:00 — 0`}
                                            />
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="flex items-center gap-1 mt-2 justify-end">
                    <span className="text-[10px] text-gray-400">Less</span>
                    {['bg-gray-50', 'bg-gray-200', 'bg-gray-400', 'bg-gray-600', 'bg-gray-900'].map((c) => (
                        <div key={c} className={`w-3 h-3 rounded-sm ${c}`} />
                    ))}
                    <span className="text-[10px] text-gray-400">More</span>
                </div>
            </div>
        </div>
    );
}

function TopUsersTable({ users }) {
    if (!users || users.length === 0) return null;
    const maxCost = Math.max(...users.map((u) => u.total_cost), 0.0001);
    return (
        <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">Top Users</h3>
                <Link to="/admin/token-usage?tab=by-user" className="text-xs text-gray-400 hover:text-gray-600">View all</Link>
            </div>
            <table className="w-full">
                <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">User</th>
                        <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Reqs</th>
                        <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Tokens</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500 w-36">Cost</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {users.map((u) => (
                        <tr key={u.user_id} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-xs truncate max-w-[160px]">
                                <Link
                                    to={`/admin/users/${encodeURIComponent(u.user_id)}`}
                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                    {u.user_id}
                                </Link>
                            </td>
                            <td className="px-4 py-2 text-right text-xs text-gray-600">{fmt(u.total_requests, 0)}</td>
                            <td className="px-4 py-2 text-right text-xs text-gray-600">{fmt(u.total_tokens, 0)}</td>
                            <td className="px-4 py-2"><CostBar value={u.total_cost} max={maxCost} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function TopAgentsTable({ agents }) {
    if (!agents || agents.length === 0) return null;
    const top = agents.slice(0, 5);
    const maxCost = Math.max(...top.map((a) => a.total_cost), 0.0001);
    return (
        <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">Top Agents</h3>
                <Link to="/admin/token-usage?tab=by-agent" className="text-xs text-gray-400 hover:text-gray-600">View all</Link>
            </div>
            <table className="w-full">
                <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Agent</th>
                        <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Reqs</th>
                        <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Avg ms</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500 w-36">Cost</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {top.map((a) => (
                        <tr key={`${a.agent_name}-${a.agent_function}`} className="hover:bg-gray-50">
                            <td className="px-4 py-2">
                                <div className="text-xs text-gray-700 truncate max-w-[140px]">{a.agent_name}</div>
                                <div className="text-[10px] text-gray-400">{a.agent_function}</div>
                            </td>
                            <td className="px-4 py-2 text-right text-xs text-gray-600">{fmt(a.total_requests, 0)}</td>
                            <td className="px-4 py-2 text-right text-xs text-gray-600">{a.avg_response_time_ms ? fmtMs(a.avg_response_time_ms) : '-'}</td>
                            <td className="px-4 py-2"><CostBar value={a.total_cost} max={maxCost} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function TopSessionsTable({ sessionStats }) {
    if (!sessionStats?.top_expensive?.length) return null;
    const sessions = sessionStats.top_expensive;
    const maxCost = Math.max(...sessions.map((s) => s.total_cost), 0.0001);
    return (
        <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">Top Expensive Sessions</h3>
                <Link to="/admin/token-usage?tab=sessions" className="text-xs text-gray-400 hover:text-gray-600">View all</Link>
            </div>
            <div className="grid grid-cols-4 gap-3 px-4 py-3 border-b border-gray-100">
                <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Sessions</p>
                    <p className="text-sm font-semibold text-gray-900">{fmt(sessionStats.total_sessions, 0)}</p>
                </div>
                <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Avg Cost</p>
                    <p className="text-sm font-semibold text-gray-900">{fmtCost(sessionStats.avg_cost_per_session)}</p>
                </div>
                <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Avg Tokens</p>
                    <p className="text-sm font-semibold text-gray-900">{fmt(sessionStats.avg_tokens_per_session, 0)}</p>
                </div>
                <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Avg Reqs</p>
                    <p className="text-sm font-semibold text-gray-900">{fmt(sessionStats.avg_requests_per_session, 1)}</p>
                </div>
            </div>
            <table className="w-full">
                <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Session</th>
                        <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Reqs</th>
                        <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Tokens</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500 w-36">Cost</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {sessions.slice(0, 5).map((s) => (
                        <tr key={s.session_id} className="hover:bg-gray-50">
                            <td className="px-4 py-2 max-w-[200px]">
                                <div className="flex items-center gap-1.5">
                                    <Link to={`/admin/sessions?session_id=${encodeURIComponent(s.session_id)}`} className="text-blue-600 hover:text-blue-800 hover:underline text-xs truncate">
                                        {s.session_name || s.session_id}
                                    </Link>
                                    {s.user_id && (
                                        <Link to={`/admin/users/${encodeURIComponent(s.user_id)}`} className="shrink-0 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded transition-colors truncate max-w-[80px]">
                                            {s.user_id}
                                        </Link>
                                    )}
                                </div>
                                {s.session_name && <span className="text-[10px] font-mono text-gray-400 truncate block">{s.session_id}</span>}
                            </td>
                            <td className="px-4 py-2 text-right text-xs text-gray-600">{s.total_requests}</td>
                            <td className="px-4 py-2 text-right text-xs text-gray-600">{fmt(s.total_tokens, 0)}</td>
                            <td className="px-4 py-2"><CostBar value={s.total_cost} max={maxCost} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const HEALTH_VARIANTS = { healthy: 'success', idle: 'warning', empty: 'danger' };

function resourceSub(rc) {
    if (!rc) return undefined;
    const parts = [];
    if (rc.builtin > 0) parts.push(`${rc.builtin} builtin`);
    if (rc.custom > 0) parts.push(`${rc.custom} custom`);
    return parts.length > 0 ? parts.join(', ') : undefined;
}

function SectionLabel({ children }) {
    return <h3 className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-2">{children}</h3>;
}

function SystemOverview({ data }) {
    if (!data) return null;
    const { tiers, total_users, usage, cache, health } = data;

    const tierSummary = tiers?.length > 0
        ? tiers.map((t) => `${t.tier_name}: ${t.count}`).join(', ')
        : null;

    return (
        <div className="space-y-4">
            <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wide">System</h2>

            <div>
                <SectionLabel>Usage & Billing</SectionLabel>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <StatCard label="Requests" value={fmt(usage?.total_requests || 0, 0)} to="/admin/token-usage" />
                    <StatCard label="Tokens" value={fmt(usage?.total_tokens || 0, 0)} to="/admin/token-usage" />
                    <StatCard label="Cost" value={fmtCost(usage?.total_cost || 0)} to="/admin/token-usage" />
                    <StatCard
                        label="Cache Hit"
                        value={cache ? `${(cache.cache_hit_rate * 100).toFixed(1)}%` : '-'}
                        sub={cache?.estimated_savings > 0 ? `~${fmtCost(cache.estimated_savings)} saved` : undefined}
                        variant={cache?.cache_hit_rate > 0.3 ? 'success' : undefined}
                        to="/admin/token-usage"
                    />
                    <StatCard
                        label="Health"
                        value={<Badge variant={HEALTH_VARIANTS[health?.status] || 'default'}>{health?.status || 'unknown'}</Badge>}
                        sub={health?.last_hour_logs != null ? `${health.last_hour_logs} logs/hr` : undefined}
                    />
                </div>
            </div>

            <div>
                <SectionLabel>Users & Content</SectionLabel>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <StatCard label="Users" value={fmt(total_users || 0, 0)} sub={tierSummary} to="/admin/user-tiers" />
                    <StatCard label="Sessions" value={fmt(data.total_sessions || 0, 0)} to="/admin/sessions" />
                    <StatCard label="Shares" value={fmt(data.total_shares || 0, 0)} to="/admin/shares" />
                    <StatCard label="Uploads" value={fmt(data.total_content_urls || 0, 0)} to="/admin/content-urls" />
                    <StatCard label="Artifacts" value={fmt(data.total_artifacts || 0, 0)} to="/admin/artifacts" />
                </div>
            </div>

            <div>
                <SectionLabel>Resources</SectionLabel>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <StatCard label="Agents" value={data.agents?.total || 0} sub={resourceSub(data.agents)} to="/admin/agents" />
                    <StatCard label="Skills" value={data.skills?.total || 0} sub={resourceSub(data.skills)} to="/admin/skills" />
                    <StatCard label="Subagents" value={data.subagents?.total || 0} sub={resourceSub(data.subagents)} to="/admin/subagents" />
                    <StatCard label="Tools" value={data.tools?.total || 0} sub={resourceSub(data.tools)} to="/admin/tools" />
                    <StatCard label="MCP Servers" value={data.mcp_servers?.total || 0} sub={resourceSub(data.mcp_servers)} to="/admin/mcp" />
                </div>
            </div>
        </div>
    );
}

function UsageMetrics({ overview, prevOverview, cache, responseTime, periodCounts }) {
    if (!overview) return null;
    const pc = periodCounts || {};
    return (
        <div>
            <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Usage Metrics</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard
                    label="Requests"
                    value={fmt(overview.total_requests, 0)}
                    change={<ChangeIndicator current={overview.total_requests} previous={prevOverview?.total_requests} />}
                    to="/admin/token-usage"
                />
                <StatCard
                    label="Tokens"
                    value={fmt(overview.total_tokens, 0)}
                    change={<ChangeIndicator current={overview.total_tokens} previous={prevOverview?.total_tokens} />}
                    to="/admin/token-usage"
                />
                <StatCard
                    label="Cost"
                    value={fmtCost(overview.total_cost)}
                    sub={`Avg ${fmtCost(overview.avg_cost_per_request)}/req`}
                    change={<ChangeIndicator current={overview.total_cost} previous={prevOverview?.total_cost} />}
                    to="/admin/token-usage"
                />
                <StatCard
                    label="Active Users"
                    value={overview.active_users}
                    change={<ChangeIndicator current={overview.active_users} previous={prevOverview?.active_users} />}
                    to="/admin/user-tiers"
                />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                <StatCard
                    label="Sessions"
                    value={fmt(pc.sessions || 0, 0)}
                    to="/admin/sessions"
                />
                <StatCard
                    label="Content URLs"
                    value={fmt(pc.content_urls || 0, 0)}
                    to="/admin/content-urls"
                />
                <StatCard
                    label="Artifacts"
                    value={fmt(pc.artifacts || 0, 0)}
                    to="/admin/artifacts"
                />
                <StatCard
                    label="Shares"
                    value={fmt(pc.shares || 0, 0)}
                    to="/admin/shares"
                />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                <StatCard
                    label="Cache Hit Rate"
                    value={cache ? `${(cache.cache_hit_rate * 100).toFixed(1)}%` : '-'}
                    sub={cache?.estimated_savings > 0 ? `~${fmtCost(cache.estimated_savings)} saved` : undefined}
                    variant={cache?.cache_hit_rate > 0.3 ? 'success' : undefined}
                />
                <StatCard
                    label="Avg Response"
                    value={responseTime?.overall?.avg_ms ? fmtMs(responseTime.overall.avg_ms) : '-'}
                    sub={responseTime?.overall?.p95 ? `p95: ${fmtMs(responseTime.overall.p95)}` : undefined}
                />
                <StatCard
                    label="p99 Latency"
                    value={responseTime?.overall?.p99 ? fmtMs(responseTime.overall.p99) : '-'}
                    sub={responseTime?.overall?.total ? `${fmt(responseTime.overall.total, 0)} measured` : undefined}
                    variant={responseTime?.overall?.p99 > 10000 ? 'warning' : undefined}
                />
                <StatCard
                    label="Slow Requests"
                    value={responseTime?.slow_requests ?? '-'}
                    sub=">5s threshold"
                    variant={responseTime?.slow_requests > 10 ? 'danger' : responseTime?.slow_requests > 0 ? 'warning' : undefined}
                />
            </div>
        </div>
    );
}


export default function AdminDashboard() {
    const [searchParams, setSearchParams] = useSearchParams();
    const preset = VALID_PERIODS.has(searchParams.get('period')) ? searchParams.get('period') : '7';
    const customStart = searchParams.get('from') || toISODate(new Date());
    const customEnd = searchParams.get('to') || toISODate(new Date());

    const updateParam = useCallback((updates) => {
        setSearchParams((prev) => {
            Object.entries(updates).forEach(([k, v]) => {
                if (v != null) prev.set(k, v);
                else prev.delete(k);
            });
            return prev;
        }, { replace: true });
    }, [setSearchParams]);

    const setPreset = (p) => {
        const u = { period: p };
        if (p !== 'custom') { u.from = null; u.to = null; }
        updateParam(u);
    };
    const handleCustomChange = (start, end) => updateParam({ from: start, to: end });

    const dateRange = useMemo(
        () => getDateRange(preset, customStart, customEnd),
        [preset, customStart, customEnd],
    );

    const prevDateRange = useMemo(() => getPrevDateRange(dateRange), [dateRange]);

    const [staticData, setStaticData] = useState(null);
    const [overview, setOverview] = useState(null);
    const [prevOverview, setPrevOverview] = useState(null);
    const [users, setUsers] = useState([]);
    const [models, setModels] = useState([]);
    const [agents, setAgents] = useState([]);
    const [sessionStats, setSessionStats] = useState(null);
    const [heatmapData, setHeatmapData] = useState(null);
    const [cache, setCache] = useState(null);
    const [responseTime, setResponseTime] = useState(null);
    const [periodCounts, setPeriodCounts] = useState(null);
    const [staticLoading, setStaticLoading] = useState(true);
    const [periodLoading, setPeriodLoading] = useState(true);

    const loadStatic = useCallback(() => {
        setStaticLoading(true);
        adminApi.getSystemStats()
            .then((stats) => setStaticData(stats))
            .catch(() => setStaticData(null))
            .finally(() => setStaticLoading(false));
    }, []);

    const loadPeriod = useCallback(() => {
        setPeriodLoading(true);
        Promise.all([
            adminApi.getTokenUsageOverview(dateRange).catch(() => null),
            adminApi.getTokenUsageOverview(prevDateRange).catch(() => null),
            adminApi.getTokenUsageByUser({ ...dateRange, limit: 5 }).catch(() => ({ users: [] })),
            adminApi.getTokenUsageByModel(dateRange).catch(() => ({ models: [] })),
            adminApi.getTokenUsageByAgent(dateRange).catch(() => ({ agents: [] })),
            adminApi.getTokenUsageSessionStats(dateRange).catch(() => null),
            adminApi.getTokenUsageHourlyHeatmap(dateRange).catch(() => null),
            adminApi.getTokenUsageCacheStats(dateRange).catch(() => null),
            adminApi.getTokenUsageResponseTime(dateRange).catch(() => null),
            adminApi.getPeriodCounts(dateRange).catch(() => null),
        ]).then(([ov, prevOv, usersRes, modelsRes, agentsRes, sess, hm, cacheRes, rtRes, pcRes]) => {
            setOverview(ov);
            setPrevOverview(prevOv);
            setUsers(usersRes?.users || []);
            setModels(modelsRes?.models || []);
            setAgents(agentsRes?.agents || []);
            setSessionStats(sess);
            setHeatmapData(hm);
            setCache(cacheRes);
            setResponseTime(rtRes);
            setPeriodCounts(pcRes);
        }).finally(() => setPeriodLoading(false));
    }, [dateRange, prevDateRange]);

    useEffect(() => { loadStatic(); }, [loadStatic]);
    useEffect(() => { loadPeriod(); }, [loadPeriod]);

    const refresh = useCallback(() => {
        loadStatic();
        loadPeriod();
    }, [loadStatic, loadPeriod]);

    if (staticLoading && periodLoading) {
        return <Spinner size="lg" text="Loading dashboard..." center />;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Dashboard</h1>
                    <p className="text-sm text-gray-500 mt-0.5">System overview and business insights</p>
                </div>
                <Button variant="secondary" size="sm" onClick={refresh}>Refresh</Button>
            </div>

            <SystemOverview data={staticData} />

            <div>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wide">Period</h2>
                </div>
                <PeriodSelector
                    preset={preset}
                    onPresetChange={setPreset}
                    customStart={customStart}
                    customEnd={customEnd}
                    onCustomChange={handleCustomChange}
                />
            </div>

            {periodLoading ? (
                <Spinner text="Loading metrics..." center />
            ) : (
                <>
                    <UsageMetrics overview={overview} prevOverview={prevOverview} cache={cache} responseTime={responseTime} periodCounts={periodCounts} />
                    <DailyUsageChart dateRange={dateRange} />

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <TopUsersTable users={users} />
                        <CostDonutChart models={models} />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <TopAgentsTable agents={agents} />
                        <HourlyHeatmapChart
                            heatmap={heatmapData?.heatmap}
                            peakHour={heatmapData?.peak_hour}
                            peakDay={heatmapData?.peak_day}
                        />
                    </div>

                    <TopSessionsTable sessionStats={sessionStats} />
                </>
            )}

        </div>
    );
}
